import { useEffect } from 'react';
import { useStickyNotification } from '@hotjar/navigation';
import { Trans } from '@hotjar/translations';
import type { CurrentUser } from '@hotjar/user';
import { useCurrentUser } from '@hotjar/user';
import type { Site } from '@hotjar/site';
import { useCurrentSiteProp } from '@hotjar/site';
import { useSubscriptionStatus } from '~/subscription-management/queries';
import { ELEMENT_NAME, trackViewElement } from '~/subscription-management/utils/tracking';
import {
  LEGACY_SUBSCRIPTION_ISSUE_ERROR,
  OVERDUE_NON_ACCOUNT_OWNER_ERROR,
} from './paymentOverdueAlertUtils';
import type { AccountSubscriptionStatus } from '~/subscription-management/queries/useSubscriptionStatus';

/**
 * Selector for `useCurrentUser` to return whether the user owns an account. (Returns `undefined`
 * if the user data is not loaded yet).
 */
const isNonAccountOwnerSelector = (currentUser: CurrentUser | undefined): boolean =>
  currentUser?.accounts == null || currentUser.accounts.length < 1;

const siteAccountIdSelector = (site: Site | undefined): number | undefined => site?.account?.id;

const subscriptionStatusSelector = (
  result: AccountSubscriptionStatus | undefined,
): AccountSubscriptionStatus['status'] | undefined => result?.status;

/**
 * Show global, non-dismissible alert for non-account owners, when the current site's
 * subscription is overdue.
 */
export const usePaymentOverdueNonAccountOwnerAlert = (): void => {
  const { hide, show } = useStickyNotification();
  const { data: isNonAccountOwner } = useCurrentUser(isNonAccountOwnerSelector);
  const { data: accountId } = useCurrentSiteProp(siteAccountIdSelector);
  const { data: subscriptionStatus } = useSubscriptionStatus(accountId, {
    enabled: isNonAccountOwner,
    select: subscriptionStatusSelector,
  });

  // check if we should show the alert.
  useEffect(() => {
    if (
      isNonAccountOwner === true &&
      typeof accountId !== 'undefined' &&
      subscriptionStatus === 'OVERDUE'
    ) {
      // hide the legacy alert, in case it also shows.
      hide(LEGACY_SUBSCRIPTION_ISSUE_ERROR);

      // show the alert.
      show(OVERDUE_NON_ACCOUNT_OWNER_ERROR, {
        kind: 'danger',
        isDismissible: false,
        text: <Trans i18nKey="checkout:alerts.payment-overdue.non-account-owner" />,
      });

      // send the view event.
      trackViewElement({
        name: ELEMENT_NAME.globalNonAccountOwnerOverdueAlert,
        accountId,
        uiPosition: 'Navigation',
      });
    } else {
      // if we shouldn't show the alert, hide it, in case it was already showing.
      hide(OVERDUE_NON_ACCOUNT_OWNER_ERROR);
    }

    return () => {
      // hide alert on unmount.
      hide(OVERDUE_NON_ACCOUNT_OWNER_ERROR);
    };
  }, [isNonAccountOwner, accountId, subscriptionStatus, hide, show]);
};
